// Signing in. spec/04 §8a.
//
// There is no "forgot password" here yet and no registration: this instance
// has one administrator, created by the setup screen. Adding a way in that
// nobody asked for is how a self-hosted product acquires its first CVE.

import { useState, type FormEvent, type ReactElement } from 'react';
import { ApiError, login } from './api.ts';
import { storeSession } from './session.ts';

export function LoginPage(): ReactElement {
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFailure(null);
    setSubmitting(true);

    const form = new FormData(event.currentTarget);
    try {
      const session = await login(
        String(form.get('email') ?? '').trim(),
        String(form.get('password') ?? ''),
      );
      storeSession({
        token: session.token,
        email: session.user.email,
        organisationName: session.organisation.name,
        expiresAt: session.expiresAt,
      });
      // A full navigation rather than a state change: the admin screen loads
      // its settings on mount, and arriving there by re-render would show a
      // form built from whatever this page happened to be holding.
      window.location.assign('/admin');
    } catch (error) {
      // Deliberately not distinguishing "no such account" from "wrong
      // password" beyond what the server already says — the server is what
      // decides, and inventing a friendlier message here is how an enumeration
      // oracle gets built by accident.
      setFailure(
        error instanceof ApiError
          ? error.message
          : 'Something between this page and the API answered instead.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="wrap">
      <h1>Sign in</h1>

      <form onSubmit={submit} className="assent">
        <p>
          <label htmlFor="email">Email address</label>
          <input id="email" name="email" type="email" autoComplete="email" required />
        </p>

        <p>
          <label htmlFor="password">Password</label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
          />
        </p>

        {failure !== null && (
          <div className="error" role="alert">
            <p>{failure}</p>
          </div>
        )}

        <div className="actions">
          <button type="submit" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </div>
      </form>
    </main>
  );
}
